import { onBeforeUnmount, reactive, Ref, ref, watch } from 'vue'

interface Visible {
  min: number
  max: number
  total: number
}

export type IntersectionObserverComposable = ReturnType<typeof useIntersectionObserver>

export function useIntersectionObserver(trackMinMaxVisible?: boolean) {
  const visibleElements = reactive<Set<number>>(new Set())
  let intersectionObserver: IntersectionObserver | null = null

  const visible = ref<Visible>()

  onBeforeUnmount(() => {
    disconnectIntersectionObserver()
  })

  const disconnectIntersectionObserver = () => {
    intersectionObserver?.disconnect()
  }

  /**
   * Initializes an intersectionObserver observer. Sets visibility status for observed elements in visibleElements whether it's visible within the root element
   * Run observeElementIntersection for each element you want to observe
   * @param stayRendered whether elements should stay rendered once initially rendered. can cause performance issues when scrolling fast as many elements then will be queued for rendering
   * @param root the intersecting object (viewport as default)
   * @param onVisibleCallback callback function that should run when an element becomes visible. uses element id and data-row (optional) as arguments
   * @param onHiddenCallback callback function when element becomes hidden
   */
  const initIntersectionObserver = (
    stayRendered?: boolean,
    root?: HTMLElement | null,
    onVisibleCallback?: (index: number, element?: Element) => void,
    onHiddenCallback?: (index: number, element?: Element) => void,
  ) => {
    disconnectIntersectionObserver()
    visibleElements.clear()
    delete visible.value

    intersectionObserver = new IntersectionObserver(
      (entries) => {
        let observableElementsWithInvalidId = 0
        entries.forEach((entry) => {
          const index = Number(entry.target.getAttribute('data-index'))

          if (index || index == 0) {
            if (entry.isIntersecting) {
              visibleElements.add(index)
              onVisibleCallback?.(index, entry.target)
            } else if (!stayRendered) {
              visibleElements.delete(index)
              onHiddenCallback?.(index, entry.target)
            }
          } else {
            observableElementsWithInvalidId++
          }
        })

        if (trackMinMaxVisible) {
          const min = getMinVisible()

          visible.value = {
            total: visibleElements.size,
            min,
            max: min + visibleElements.size,
          }
        }

        if (observableElementsWithInvalidId) {
          console.log(
            `Found ${observableElementsWithInvalidId} elements that couldn't be observed due to missing or invalid attribute "data-index". "data-index" should be numeric`,
          )
        }
      },
      { root },
    )
  }

  const getMinVisible = () => {
    let min: number | undefined
    for (const index of visibleElements) {
      if (min === undefined || index < min) {
        min = index
      }
    }
    return min as number
  }

  // const getMaxVisible = () => {
  // let max: number | undefined
  // for (const index of visibleElements) {
  //   if ((!trackMinVisibleIndex || index >= trackMinVisibleIndex) && (max === undefined || index > max)) {
  //     max = index
  //   }
  // }
  // return max as number
  // }

  /**
   * Observes when elements comes in or out of view
   */
  const observeAll = (elements: Element[] | NodeListOf<Element> | undefined) => {
    if (!intersectionObserver) {
      console.warn("Error: can't observe elements. Observer not initialized")
    }

    elements?.forEach((element) => element && intersectionObserver?.observe(element))
  }

  const elementIsVisible = (elementId: number) => visibleElements.has(elementId)

  return {
    elementIsVisible,
    initIntersectionObserver,
    observeAll,
    disconnectIntersectionObserver,
    visible,
  }
}
